"use client";

import * as React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, Wrench, ShieldAlert, Clock, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "How do I know if my water heater needs repair or full replacement?",
  answer: "Most standard tank water heaters last 8 to 12 years. If your unit is under 8 years old and experiencing a faulty thermostat, failed heating element, or valve leak, a repair is usually the most cost-effective option. If the tank itself is rusted or leaking from the base, replacement is necessary to prevent severe water damage."
}, {
  id: "item-2",
  question: "Why is my water heater making popping or rumbling noises?",
  answer: "Popping and rumbling sounds are caused by mineral sediment buildup at the bottom of your tank. As water gets trapped beneath the sediment layer and boils, it creates popping sounds. Flushing your water heater clears the sediment, improves efficiency, and extends the lifespan of the tank."
}, {
  id: "item-3",
  question: "What should I do immediately if my water heater is leaking?",
  answer: "Turn off the power supply immediately (switch off the circuit breaker for electric heaters, or turn the gas valve to 'OFF' for gas heaters). Next, shut off the cold water supply valve located on top of the tank to stop incoming water flow, and call our emergency plumbing team right away."
}, {
  id: "item-4",
  question: "Why am I running out of hot water much faster than usual?",
  answer: "For electric water heaters, this typically means the lower heating element or its thermostat has failed, leaving only the top element working. In gas heaters, it could indicate a damaged dip tube that is mixing incoming cold water directly with outgoing hot water at the top of the tank."
}, {
  id: "item-5",
  question: "How fast can an emergency plumber arrive in Jonesboro for repairs?",
  answer: "We offer 24/7 emergency water heater response across Jonesboro and surrounding Clayton County communities. Our fully stocked service vehicles carry universal replacement elements, valves, and thermostats so we can resolve most urgent repairs in a single visit."
}];
export default function HeaterFAQ() {
  return <section id="waterheaterrepairfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-semibold text-xs uppercase tracking-wide">
            <HelpCircle className="size-3.5 mr-1 text-accent" />
            Common Questions
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Frequently Asked Questions About Water Heater Repair
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            Quick answers to the most common water heater problems, emergency steps, and repair timelines for Jonesboro homeowners.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion List */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                  <AccordionItem value={item.id} className="border border-border bg-card rounded-xl px-5 py-1 transition-colors hover:border-primary/40 shadow-xs">
                    <AccordionTrigger className="text-base sm:text-lg font-semibold text-left py-4 hover:no-underline text-card-foreground">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>

          {/* Quick Help / Emergency Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="bg-primary text-primary-foreground border-0 shadow-lg rounded-2xl p-6 sm:p-8">
              <CardContent className="p-0 flex flex-col gap-5">
                <div className="inline-flex size-12 items-center justify-center rounded-xl bg-primary-foreground/15 text-primary-foreground">
                  <ShieldAlert className="size-6" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary-foreground mb-2">
                    Have an Urgent Water Heater Leak?
                  </h3>
                  <p className="text-primary-foreground/85 text-sm sm:text-base leading-relaxed">
                    Do not wait for water damage to spread. Our licensed plumbers in Jonesboro provide 24/7 emergency water heater repair and tank draining services.
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md w-full justify-center">
                    <a href="tel:4708479076" className="flex items-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call for Emergency Repair</span>
                    </a>
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-primary-foreground/20 text-xs text-primary-foreground/80">
                  <div className="flex items-center gap-1.5">
                    <Clock className="size-4 text-accent" />
                    <span>24/7 Rapid Response</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Wrench className="size-4 text-accent" />
                    <span>All Makes & Models</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-secondary text-secondary-foreground rounded-2xl p-6 border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-foreground text-base mb-1">Need a full unit replacement?</h4>
                <p className="text-muted-foreground text-sm">Explore our tank and tankless water heater installation options.</p>
              </div>
              <Button asChild variant="outline" className="shrink-0 font-medium">
                <Link to="/water-heater/" className="inline-flex items-center gap-2">
                  <span>View Services</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}